import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AuthSession, PermissionCode, SessionInfo } from '../types';
import { api, setSessionExpiredHandler, tokenStore } from './api';
import { AuthContext } from './auth-context-value';
import { applyTheme } from './theme';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionInfo | null>(null);
  // Só há o que carregar se existir token guardado — sem isso a tela de login
  // já entra pronta, e o efeito abaixo não precisa mexer no estado à toa.
  const [loading, setLoading] = useState(() => Boolean(tokenStore.access));

  const applySession = useCallback((next: SessionInfo | null) => {
    setSession(next);
    applyTheme(next?.workspace.theme);
  }, []);

  // Sair recarrega a tela: o cache das consultas (leads, pipelines…) não pode
  // sobrar para a próxima pessoa que entrar neste navegador.
  const logout = useCallback(() => {
    tokenStore.clear();
    applySession(null);
    if (window.location.pathname !== '/login') window.location.replace('/login');
  }, [applySession]);

  // Restaura a sessão a partir do token guardado (F5 não desloga).
  useEffect(() => {
    setSessionExpiredHandler(() => applySession(null));
    if (!tokenStore.access) return;

    let active = true;
    api
      .get<SessionInfo>('/auth/me')
      .then(({ data }) => active && applySession(data))
      .catch(() => active && logout())
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [applySession, logout]);

  const startSession = useCallback(
    (data: AuthSession) => {
      tokenStore.save(data.accessToken, data.refreshToken);
      applySession(sessionFrom(data));
    },
    [applySession],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const { data } = await api.post<AuthSession>('/auth/login', { email, password });
      startSession(data);
    },
    [startSession],
  );

  const switchWorkspace = useCallback(async (workspaceId: string) => {
    const { data } = await api.post<AuthSession>('/auth/switch-workspace', {
      tenantId: workspaceId,
    });
    // Recarrega na mesma seção, sem os parâmetros da empresa anterior (?p=, filtros):
    // assim nada do cache nem do endereço da empresa anterior aparece na nova.
    tokenStore.save(data.accessToken, data.refreshToken);
    window.location.replace(window.location.pathname.split('/').slice(0, 2).join('/') || '/');
  }, []);

  const refreshSession = useCallback(async () => {
    const { data } = await api.get<SessionInfo>('/auth/me');
    applySession(data);
  }, [applySession]);

  const isAdmin = session?.access.isAdmin ?? false;
  const can = useCallback(
    (...codes: PermissionCode[]) =>
      isAdmin || codes.some((code) => session?.access.permissions.includes(code)),
    [isAdmin, session],
  );

  const value = useMemo(
    () => ({
      session,
      loading,
      login,
      logout,
      switchWorkspace,
      startSession,
      refreshSession,
      can,
      isAdmin,
    }),
    [
      session,
      loading,
      login,
      logout,
      switchWorkspace,
      startSession,
      refreshSession,
      can,
      isAdmin,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

const sessionFrom = (auth: AuthSession): SessionInfo => ({
  user: auth.user,
  workspace: auth.workspace,
  workspaces: auth.workspaces,
  access: auth.access,
});
